
const BLEED_IN_DEFAULT = 0.125;
const SAFE_IN_DEFAULT = 0.125;

/**
 * Generate a 1:1 scale die-cut PDF template for a label.
 * Includes the shape outline, bleed line, safe zone, and dimension annotations.
 *
 * Page size = label size + 1" margin all around (in inches).
 * Coordinates use unit "in" (inches).
 */
export async function generateDieTemplate({
  shape = "Rectangle",
  corners = "Rounded",
  heightIn,
  widthIn,
  productName = "Label",
  materialName = "",
  color = "",
  sku = "",
}) {
  const h = Math.max(0.25, parseFloat(heightIn) || 2);
  const w = Math.max(0.25, parseFloat(widthIn) || 3);
  const margin = 1.0; // 1" margin around label
  const bleed = 0.125; // 1/8" bleed
  const safe = 0.125; // 1/8" safe zone inside

  const pageW = w + margin * 2;
  const pageH = h + margin * 2;

  const { default: jsPDF } = await import("jspdf");
  const doc = new jsPDF({
    unit: "in",
    format: [pageW, pageH],
    orientation: pageW >= pageH ? "landscape" : "portrait",
  });

  // Header strip
  doc.setFontSize(10);
  doc.setTextColor(0, 75, 135); // brand navy
  doc.setFont("helvetica", "bold");
  doc.text("KoltPrinting — Die-Cut Template (1:1 scale)", margin, 0.35);

  doc.setFontSize(7);
  doc.setTextColor(71, 85, 105);
  doc.setFont("helvetica", "normal");
  doc.text(
    `${productName}  •  ${w}" × ${h}"  •  ${shape}${corners && shape === "Rectangle" ? ` (${corners} corners)` : ""}`,
    margin,
    0.52,
  );
  if (materialName || color) {
    doc.text(`${materialName}${materialName && color ? " • " : ""}${color}`, margin, 0.66);
  }
  if (sku) {
    doc.setFont("helvetica", "bold");
    doc.text(sku, pageW - margin, 0.35, { align: "right" });
    doc.setFont("helvetica", "normal");
  }

  // Label center
  const cx = pageW / 2;
  const cy = pageH / 2;
  const x = cx - w / 2;
  const y = cy - h / 2;

  // Bleed line (red dashed) — outside
  doc.setDrawColor(220, 38, 38);
  doc.setLineDashPattern([0.05, 0.05], 0);
  doc.setLineWidth(0.008);
  drawShape(doc, shape, corners, x - bleed, y - bleed, w + bleed * 2, h + bleed * 2);

  // Cut line (solid black) — actual die line
  doc.setDrawColor(0, 0, 0);
  doc.setLineDashPattern([], 0);
  doc.setLineWidth(0.012);
  drawShape(doc, shape, corners, x, y, w, h);

  // Safe zone (blue dashed) — inside
  doc.setDrawColor(0, 75, 135);
  doc.setLineDashPattern([0.04, 0.04], 0);
  doc.setLineWidth(0.006);
  drawShape(doc, shape, corners, x + safe, y + safe, w - safe * 2, h - safe * 2);

  // Reset line style
  doc.setLineDashPattern([], 0);

  // Dimension lines (outside label, top + left)
  doc.setDrawColor(100, 116, 139);
  doc.setLineWidth(0.005);

  // Width dim line at y - 0.35
  const dimWY = y - 0.35;
  doc.line(x, dimWY, x + w, dimWY);
  doc.line(x, dimWY - 0.05, x, dimWY + 0.05);
  doc.line(x + w, dimWY - 0.05, x + w, dimWY + 0.05);
  doc.setFontSize(9);
  doc.setTextColor(15, 23, 42);
  doc.setFont("helvetica", "bold");
  doc.text(`${w}"`, x + w / 2, dimWY - 0.05, { align: "center" });

  // Height dim line at x - 0.35
  const dimHX = x - 0.35;
  doc.line(dimHX, y, dimHX, y + h);
  doc.line(dimHX - 0.05, y, dimHX + 0.05, y);
  doc.line(dimHX - 0.05, y + h, dimHX + 0.05, y + h);
  // Rotated text for height
  doc.text(`${h}"`, dimHX - 0.05, y + h / 2, { align: "center", angle: 90 });

  // Legend (bottom)
  const legendY = pageH - 0.5;
  doc.setFontSize(7);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(71, 85, 105);
  drawLegendDot(doc, margin, legendY, [220, 38, 38], true);
  doc.text("Bleed (0.125\")", margin + 0.18, legendY + 0.03);
  drawLegendDot(doc, margin + 1.2, legendY, [0, 0, 0]);
  doc.text("Cut line (die)", margin + 1.38, legendY + 0.03);
  drawLegendDot(doc, margin + 2.4, legendY, [0, 75, 135], true);
  doc.text("Safe zone (0.125\")", margin + 2.58, legendY + 0.03);

  // Footer
  doc.setFontSize(6);
  doc.setTextColor(148, 163, 184);
  doc.text(
    "Generated by KoltPrinting.com — 1:1 scale, do not scale to fit. Verify all dimensions before submitting artwork.",
    cx,
    pageH - 0.2,
    { align: "center" },
  );

  const fileSafeName = (productName || "label").toLowerCase().replace(/[^a-z0-9]+/g, "-");
  doc.save(`KoltPrinting-die-${fileSafeName}-${w}x${h}.pdf`);
}

function drawShape(doc, shape, corners, x, y, w, h) {
  const s = (shape || "Rectangle").toLowerCase();
  if (s === "circle") {
    const r = Math.min(w, h) / 2;
    doc.circle(x + w / 2, y + h / 2, r, "S");
  } else if (s === "oval") {
    doc.ellipse(x + w / 2, y + h / 2, w / 2, h / 2, "S");
  } else if (s === "square") {
    const side = Math.min(w, h);
    const sx = x + (w - side) / 2;
    const sy = y + (h - side) / 2;
    if (corners === "Square") {
      doc.rect(sx, sy, side, side, "S");
    } else {
      doc.roundedRect(sx, sy, side, side, side * 0.12, side * 0.12, "S");
    }
  } else {
    // Rectangle
    if (corners === "Square") {
      doc.rect(x, y, w, h, "S");
    } else {
      const r = Math.min(w, h) * 0.12;
      doc.roundedRect(x, y, w, h, r, r, "S");
    }
  }
}

function drawLegendDot(doc, x, y, rgb, dashed) {
  doc.setDrawColor(rgb[0], rgb[1], rgb[2]);
  doc.setLineWidth(0.012);
  if (dashed) doc.setLineDashPattern([0.03, 0.03], 0);
  else doc.setLineDashPattern([], 0);
  doc.line(x, y + 0.03, x + 0.14, y + 0.03);
  doc.setLineDashPattern([], 0);
}

/**
 * Generate a 1:1 scale die-cut SVG template.
 * Imports cleanly into Adobe Illustrator (.ai) and CorelDRAW (.cdr) —
 * both use SVG as a lossless interchange format for die/cut lines.
 *
 * Returns the SVG string; the caller decides how to deliver (download,
 * copy to clipboard, etc.).
 *
 * SVG units are inches (1 unit = 1 inch) via viewBox + width/height.
 */
export function buildDieSvg({
  shape = "Rectangle",
  corners = "Rounded",
  heightIn,
  widthIn,
  productName = "Label",
  materialName = "",
  color = "",
  sku = "",
  bleed = BLEED_IN_DEFAULT,
  safe = SAFE_IN_DEFAULT,
}) {
  const h = Math.max(0.25, parseFloat(heightIn) || 2);
  const w = Math.max(0.25, parseFloat(widthIn) || 3);
  const margin = 1.0;
  const pageW = w + margin * 2;
  const pageH = h + margin * 2;
  const cx = pageW / 2;
  const cy = pageH / 2;
  const x = cx - w / 2;
  const y = cy - h / 2;

  const shapePath = (offX, offY, wIn, hIn) => {
    const s = (shape || "Rectangle").toLowerCase();
    const cxi = offX + wIn / 2;
    const cyi = offY + hIn / 2;
    if (s === "circle") {
      const r = Math.min(wIn, hIn) / 2;
      return `<circle cx="${cxi}" cy="${cyi}" r="${r}" />`;
    }
    if (s === "oval") {
      return `<ellipse cx="${cxi}" cy="${cyi}" rx="${wIn / 2}" ry="${hIn / 2}" />`;
    }
    if (s === "square") {
      const side = Math.min(wIn, hIn);
      const sx = cxi - side / 2;
      const sy = cyi - side / 2;
      const sr = corners === "Square" ? 0 : side * 0.12;
      return `<rect x="${sx}" y="${sy}" width="${side}" height="${side}" rx="${sr}" ry="${sr}" />`;
    }
    const rr = corners === "Square" ? 0 : Math.min(wIn, hIn) * 0.12;
    return `<rect x="${offX}" y="${offY}" width="${wIn}" height="${hIn}" rx="${rr}" ry="${rr}" />`;
  };

  // Each colored path becomes its own layer group — Illustrator and CorelDRAW
  // both pick these up as separate named layers on import.
  const bleedShape = shapePath(x - bleed, y - bleed, w + bleed * 2, h + bleed * 2);
  const dieShape = shapePath(x, y, w, h);
  const safeShape = shapePath(x + safe, y + safe, w - safe * 2, h - safe * 2);

  const header = `${productName} — ${w}" × ${h}" ${shape}${corners && shape === "Rectangle" ? ` (${corners} corners)` : ""}`;
  const materialLine = [materialName, color].filter(Boolean).join(" • ");

  return `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg"
     width="${pageW}in" height="${pageH}in"
     viewBox="0 0 ${pageW} ${pageH}"
     data-kolt-product="${escapeXml(productName)}"
     data-kolt-sku="${escapeXml(sku)}">
  <title>${escapeXml(header)}</title>
  <desc>Kolt Printing die-cut template. 1:1 scale. Bleed 0.125", safe 0.125". Import into Illustrator / CorelDRAW as separate layers.</desc>
  <g id="Bleed" fill="none" stroke="#DC2626" stroke-width="0.008" stroke-dasharray="0.05 0.05">
    ${bleedShape}
  </g>
  <g id="Die-Cut-Line" fill="none" stroke="#000000" stroke-width="0.012">
    ${dieShape}
  </g>
  <g id="Safe-Zone" fill="none" stroke="#004B87" stroke-width="0.006" stroke-dasharray="0.04 0.04">
    ${safeShape}
  </g>
  <g id="Metadata" font-family="Helvetica, Arial, sans-serif" fill="#333F48">
    <text x="${margin}" y="${0.4}" font-size="0.12" font-weight="bold">KoltPrinting — Die-Cut Template (1:1 scale)</text>
    <text x="${margin}" y="${0.56}" font-size="0.09">${escapeXml(header)}</text>
    ${materialLine ? `<text x="${margin}" y="${0.7}" font-size="0.08" fill="#475569">${escapeXml(materialLine)}</text>` : ""}
    ${sku ? `<text x="${pageW - margin}" y="${0.4}" font-size="0.09" font-weight="bold" text-anchor="end">${escapeXml(sku)}</text>` : ""}
    <text x="${cx}" y="${pageH - 0.15}" font-size="0.07" fill="#94A3B8" text-anchor="middle">Generated by KoltPrinting.com — Do not scale. Verify dimensions before submitting artwork.</text>
  </g>
</svg>`;
}

/**
 * Download the SVG die template as a file. Compatible with Adobe Illustrator
 * (.ai) and CorelDRAW (.cdr) — both open .svg natively as editable vectors.
 */
export function generateDieSvg(opts) {
  const svg = buildDieSvg(opts);
  const w = Math.max(0.25, parseFloat(opts.widthIn) || 3);
  const h = Math.max(0.25, parseFloat(opts.heightIn) || 2);
  const fileSafeName = (opts.productName || "label").toLowerCase().replace(/[^a-z0-9]+/g, "-");
  const blob = new Blob([svg], { type: "image/svg+xml" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `KoltPrinting-die-${fileSafeName}-${w}x${h}.svg`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(url), 1500);
}

function escapeXml(s) {
  return String(s || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;");
}

/**
 * Popular standard label sizes for quick-download panel.
 * Keeps the promise of "pre-generated templates" for the most-requested
 * sizes — 1×2, 2×3, 3×4 — without shipping binary CDR/AI files.
 */
export const POPULAR_DIE_SIZES = [
  { w: 1, h: 2, label: "1\" × 2\"" },
  { w: 2, h: 3, label: "2\" × 3\"" },
  { w: 3, h: 4, label: "3\" × 4\"" },
];

